import './CustomSegments.scss';
import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Button } from '@Components/Button';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Appearance, Size } from '@Components/designConstants';

interface SegmentGroupCardProps {
  groupName: string;
  segments: GroupedSegment[];
  hasEdits: boolean;
  onEditSegmentName: (newVal: string, segmentId: number | string) => void;
  addNewGroupedSegment: (segmentName: string) => {
    segmentId: number | string;
    segmentName: string;
  };
  newGroupedSegments: Record<string, string>;
  editedSegmentNamesMap: Record<string, string | undefined>;
  deleteNewGroupedSegment: (groupName: string, segmentId: number | string) => void;
  deleteGroupedSegment: (segmentId: number | string) => void;
  isInEditMode: boolean;
  setIsInEditMode: (newVal: boolean) => void;
}

export const SegmentGroupCard = ({
  groupName,
  segments,
  hasEdits,
  onEditSegmentName,
  addNewGroupedSegment,
  newGroupedSegments,
  editedSegmentNamesMap,
  deleteNewGroupedSegment,
  deleteGroupedSegment,
  isInEditMode,
  setIsInEditMode
}: SegmentGroupCardProps) => {
  const newSegments = useMemo(
    () =>
      Object.entries(newGroupedSegments || {}).map(([segmentId, segmentName]) => ({
        segmentId,
        segmentName
      })),
    [newGroupedSegments]
  );

  const existingSegmentIds = useMemo(
    () => new Set(segments.map(s => s.segmentId.toString())),
    [segments]
  );
  const [nameValidationErrors, setNameValidationErrors] = useState<Record<string, string>>({});

  const validateSegmentName = (value: string, segmentId: string): boolean => {
    if (value.includes("'")) {
      setNameValidationErrors(current => ({
        ...current,
        [segmentId]: 'Segment names cannot contain apostrophes.'
      }));
      return false;
    }
    if (value.includes('\\')) {
      setNameValidationErrors(current => ({
        ...current,
        [segmentId]: 'Segment names cannot contain backslashes.'
      }));
      return false;
    }
    setNameValidationErrors(current => {
      const updated = { ...current };
      delete updated[segmentId];
      return updated;
    });
    return true;
  };

  return (
    <div className='segment-container'>
      <div className='segment-header'>
        <div className='segment-title'>
          <div className='segment-type-label'>
            Segment Group
            <InfoTooltip>
              <p className='custom-segments-tooltip'>
                All names listed below are custom segments with values Yes and No.
              </p>
            </InfoTooltip>
          </div>
          {!isInEditMode && (
            <IconButton
              appearance={Appearance.EMPTY}
              onClick={() => {
                setIsInEditMode(true);
              }}
            >
              <MdEdit />
            </IconButton>
          )}
        </div>
        <div className='segment-name-container'>
          <div className='segment-name'>{groupName}</div>
        </div>
      </div>
      <div className='segment-body'>
        <div className='segment-values'>
          {!isInEditMode &&
            segments.map(({ segmentId, segmentName }) => (
              <div className='option' key={segmentId}>
                {segmentName}
              </div>
            ))}
          {isInEditMode &&
            [...segments, ...newSegments].map(({ segmentId, segmentName }) => {
              const nameValidationError = nameValidationErrors[segmentId.toString()];
              return (
                <div className='editable-value' key={segmentId}>
                  <Form.Group className='segment-name-input-group'>
                    <div className='segment-value-input-row'>
                      <Form.Control
                        className='segment-value-input'
                        isInvalid={Boolean(nameValidationError)}
                        size='sm'
                        placeholder='New Segment'
                        type='text'
                        value={editedSegmentNamesMap[segmentId.toString()] ?? segmentName}
                        onChange={e => {
                          if (validateSegmentName(e.currentTarget.value, segmentId.toString())) {
                            onEditSegmentName(e.currentTarget.value, segmentId);
                          }
                        }}
                        onBlur={e => {
                          onEditSegmentName(e.currentTarget.value.trim(), segmentId);
                        }}
                      />
                      <IconButton
                        appearance={Appearance.EMPTY}
                        onClick={() => {
                          if (existingSegmentIds.has(segmentId.toString())) {
                            deleteGroupedSegment(segmentId);
                          } else {
                            deleteNewGroupedSegment(groupName, segmentId);
                          }
                        }}
                      >
                        <MdDelete />
                      </IconButton>
                    </div>
                    {nameValidationError && (
                      <Form.Control.Feedback type='invalid'>
                        {nameValidationError}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </div>
              );
            })}
          {isInEditMode && (
            <Button
              size={Size.LG}
              leadingElement={<MdAdd />}
              onClick={() => addNewGroupedSegment(groupName)}
            />
          )}
        </div>
      </div>
      {hasEdits && (
        <div className='unsaved-changes-container'>
          <div className='unsaved-changes'>Unsaved changes</div>
        </div>
      )}
    </div>
  );
};
